import {
  CreateBookingRequest,
  CreateBookingV2Request,
  CreatePaymentRequest,
  CreateTableServiceOrderRequest,
  FetchActivePeriods,
  FetchBookedTablesByPeriod,
  FetchBookingsRequest,
  FetchCategories,
  FetchLockedTablesByPeriod,
  FetchProducts,
  FetchThemesRequest,
  FetchTicketEvent,
  FetchTicketEvents,
  FetchTicketOrder,
  FetchTicketOrderReceipt,
  FetchVenueFeatureFlag,
  IGuestApi,
  PagedByVenueRequest,
  PlaceTicketOrder,
  ResendTicketOrderReceipt,
  UpdateCustomerRequest,
  UpdateUserArgs,
  VenueRequest,
} from "./IGuestApi";
import { UrlBuilder } from "@greeter/url";
import {
  GreeterEvent,
  Venue,
  Activity,
  VenueActivity,
  NewsItem,
  DaysliceConfig,
  Theme,
  Product,
  Order,
  FloorPlan,
  MusicGenre,
  StripeEphemeralKey,
  TableId,
  Payment,
  Customer,
  CustomerSchema,
  BookingSettings,
  LockedTable,
  Menu,
  TableServiceOrder,
  TableServiceSettings,
  SpecialOpeningHours,
  SpecialPricePeriod,
  SpecialPricePeriodSchema,
  MenuSchema,
  Bundle,
  BookingSchema,
  BundlesSchema,
  TicketOrderReceiptSchema,
  VenueFeatureFlagSchema,
  VenueSchema,
  LockedTableSchema,
  TableIdSchema,
  ActivePeriodsSchema,
  ProductSchema,
  Booking,
  GreeterEventSchema,
  ThemeSchema,
  CategorySchema,
  TicketEventSchema,
  TicketOrderSchema,
} from "@greeter/core";
import { ApiVersions, BaseProxy, EmptyBody, withApiVersion } from "./ProxyBase";
import {
  FloorPlanDeserializer,
  IDeserializer,
  OrderDeserializer,
} from "./deserializers";
import { PagedLocationRequest, PageRequest } from "./Requests";
import { uniq } from "lodash";
import { IAuthentication } from "@greeter/auth";
import { Config } from "@greeter/config";
import { DatePeriod, TimeOfDay, toISODateString } from "@greeter/date";
import { ManySpecialOpeningHoursDeserializer } from "./deserializers/SpecialOpeningHoursDeserializer";

export class GuestApi extends BaseProxy implements IGuestApi {
  url: URL;

  constructor(
    config: Config,
    authentication: IAuthentication,
    private orderDeserializer: IDeserializer<Order> = new OrderDeserializer(),
    private floorPlanDeserializer: IDeserializer<FloorPlan> = new FloorPlanDeserializer()
  ) {
    super(config, authentication);
    this.url = new URL(`${this.getBaseUrl()}/api`);
  }

  resendTicketOrderReceipt: ResendTicketOrderReceipt = async (request) => {
    const url = new UrlBuilder(this.getUrl())
      .addPath("ticketOrders")
      .addPath(request.id)
      .addPath("resendReceipt")
      .build();

    await this.post(url, EmptyBody, { authorize: true, ignoreResult: true });
  };

  fetchBookedTablesByPeriod: FetchBookedTablesByPeriod = async (request) => {
    const url = new UrlBuilder(this.getUrl())
      .addPath("venues")
      .addPath(request.venueId)
      .addPath("bookedTables")
      .addPath(request.period.from.toISOString())
      .addPath(request.period.to.toISOString())
      .build();

    const response = await this.get(url);
    const json = await response.json();
    return json.map(TableIdSchema.parse);
  };

  fetchLockedTablesByPeriod: FetchLockedTablesByPeriod = async (request) => {
    const url = new UrlBuilder(this.getUrl())
      .addPath("venues")
      .addPath(request.venueId)
      .addPath("lockedTables")
      .addPath(request.period.from.toISOString())
      .addPath(request.period.to.toISOString())
      .build();

    const response = await this.get(url);
    const json = await response.json();
    return json.map(LockedTableSchema.parse);
  };

  fetchTicketEvent: FetchTicketEvent = async (request) => {
    const url = new UrlBuilder(this.getUrl())
      .addPath("ticketEvents")
      .addPath(request.slug)
      .build();

    const response = await this.get(url);
    const json = await response.json();
    return TicketEventSchema.parse(json);
  };

  fetchTicketEvents: FetchTicketEvents = async (request) => {
    const url = new UrlBuilder(this.getUrl())
      .addPath("ticketEvents")
      .addQuery("venueId", request.venueId)
      .addQuery("from", request.period.from.toUTCString())
      .addQuery("to", request.period.to.toUTCString())
      .build();

    const response = await this.get(url);
    const json = await response.json();

    if (!Array.isArray(json)) {
      throw new Error("Wrong type for TicketEvents, we need an array");
    }

    const mapped = json.map((j) => TicketEventSchema.parse(j));

    return mapped;
  };

  placeTicketOrder: PlaceTicketOrder = async (request) => {
    const url = new UrlBuilder(this.getUrl()).addPath("ticketOrders").build();

    const response = await this.post(url, JSON.stringify(request), {
      authorize: true,
    });
    const json = await response.json();
    return TicketOrderSchema.parse(json);
  };

  fetchTicketOrder: FetchTicketOrder = async (request) => {
    const url = new UrlBuilder(this.getUrl())
      .addPath("ticketOrders")
      .addPath(request.id)
      .build();

    const response = await this.get(url, { authorize: true });
    const json = await response.json();
    return TicketOrderSchema.parse(json);
  };

  fetchTicketOrderReceipt: FetchTicketOrderReceipt = async (request) => {
    const url = new UrlBuilder(this.getUrl())
      .addPath("ticketOrders")
      .addPath(request.id)
      .addPath("receipt")
      .build();

    const response = await this.get(url, { authorize: true });
    const json = await response.json();
    return TicketOrderReceiptSchema.parse(json);
  };

  fetchCategories: FetchCategories = async (request) => {
    const url = new UrlBuilder(this.getUrl())
      .addPath("categories")
      .addQuery("ids", request.categoryIds)
      .build();

    const response = await this.get(url, { authorize: true });
    const json = await response.json();
    return json.map(CategorySchema.parse);
  };

  fetchProducts: FetchProducts = async (request) => {
    const url = new UrlBuilder(this.getUrl())
      .addPath("products")
      .addQuery("ids", request.productIds)
      .build();

    const response = await this.get(url, { authorize: true });
    const json = await response.json();

    return json.map(ProductSchema.parse);
  };

  fetchActivePeriods: FetchActivePeriods = async (request) => {
    const url = new UrlBuilder(this.getUrl())
      .addPath("activePeriods")
      .addQuery("productIds", request.productIds)
      .build();

    const result = await this.get(url);
    const json = await result.json();

    return ActivePeriodsSchema.parse(json);
  };

  fetchVenueFeatureFlag: FetchVenueFeatureFlag = async (request) => {
    const url = new UrlBuilder(this.getUrl())
      .addPath("venues")
      .addPath(request.venueId)
      .addPath("featureFlags")
      .addPath(request.name)
      .build();

    const result = await this.get(url);
    const json = await result.json();

    return VenueFeatureFlagSchema.parse(json);
  };

  async createBookingV2(request: CreateBookingV2Request): Promise<Booking> {
    const url = new UrlBuilder(this.getUrl()).addPath("bookings").build();
    const response = await this.post(url, JSON.stringify(request), {
      authorize: true,
    });
    const result = await response.json();
    return BookingSchema.parse(result);
  }

  async fetchBundlesByVenue(venueId: string): Promise<Array<Bundle>> {
    const url = new UrlBuilder(this.getUrl())
      .addPath("bundles")
      .addQuery("venueId", venueId)
      .build();
    const result = await this.get(url);
    const json = await result.json();
    return BundlesSchema.parse(json);
  }

  async fetchCities(country: string) {
    const url = new UrlBuilder(this.getUrl())
      .addPath("venues")
      .addPath("cities")
      .addPath(country)
      .build();

    const response = await this.get(url);
    const result = await response.json();

    return result as string[];
  }

  async createTableServiceOrder({
    venueId,
    ...request
  }: CreateTableServiceOrderRequest) {
    const url = new UrlBuilder(this.getUrl())
      .addPath("tableServiceOrders")
      .addPath(venueId)
      .build();

    const response = await this.post(
      url,
      JSON.stringify({ ...request.tableId, orderLines: request.orderLines }),
      { authorize: true }
    );
    const result = await response.json();

    return result as TableServiceOrder;
  }

  async fetchTableServiceOrderPdf(id: string) {
    const url = new UrlBuilder(this.getUrl())
      .addPath("tableServiceOrders")
      .addPath(id)
      .addPath("pdf")
      .build();

    const result = await this.getFileWithAuth(url, (init) => ({
      headers: { ...init.headers, "Content-Type": "application/pdf" },
    }));

    return result;
  }

  async fetchTableServiceOrder(id: string) {
    const url = new UrlBuilder(this.getUrl())
      .addPath("tableServiceOrders")
      .addPath(id)
      .build();

    const response = await this.get(url, { authorize: true });
    const result = await response.json();

    return result as TableServiceOrder;
  }

  async fetchTableServiceOrders(request: PageRequest) {
    const url = new UrlBuilder(this.getUrl())
      .addQuery("page", request.page)
      .addQuery("pageSize", request.pageSize)
      .build();

    const response = await this.get(url, { authorize: true });
    const result = await response.json();

    return result as TableServiceOrder[];
  }

  async fetchTableServiceSettings(venueId: string) {
    const url = new UrlBuilder(this.getUrl())
      .addPath("tableServiceSettings")
      .addQueries({ venueId })
      .build();

    const response = await this.get(url);
    const result = await response.json();

    return result as TableServiceSettings;
  }

  async fetchMenu(venueId: string): Promise<Menu> {
    const url = new UrlBuilder(this.getUrl())
      .addPath("menus")
      .addQuery("venueId", venueId)
      .build();

    const response = await this.get(url);
    const result = await response.json();

    return MenuSchema.parse(result);
  }

  async fetchBookingSettings(venueId: string): Promise<BookingSettings> {
    const url = new UrlBuilder(this.getUrl())
      .addPath("bookingSettings")
      .addPath("byVenue")
      .addPath(venueId)
      .build();

    const response = await this.get(url);
    const result = await response.json();

    return { buffer: TimeOfDay.parse(result.buffer) } as BookingSettings;
  }

  async deleteUser() {
    const url = new UrlBuilder(this.getUrl()).addPath("user").build();

    await this.delete(url, EmptyBody, { authorize: true });
  }

  async fetchCustomer() {
    const url = new UrlBuilder(this.getUrl()).addPath("customer").build();

    const response = await this.get(url, { authorize: true });
    const json = await response.json();
    return CustomerSchema.parse(json);
  }

  async subscribeToNotifications(registrationsId: string): Promise<void> {
    const url = new UrlBuilder(this.getUrl())
      .addPath("pushNotificationSubscriptions")
      .addPath(registrationsId)
      .build();

    await this.post(url, EmptyBody, { authorize: true });
  }

  async createPayment({
    orderId,
    redirectUrl,
  }: CreatePaymentRequest): Promise<Payment> {
    const url = new UrlBuilder(this.getUrl())
      .addPath("orders")
      .addPath(orderId)
      .addPath("payment")
      .addQuery("redirectUrl", redirectUrl)
      .build();

    const response = await (
      await this.post(url, EmptyBody, { authorize: true })
    ).json();

    return response as Payment;
  }

  async createBooking(request: CreateBookingRequest) {
    const url = new UrlBuilder(this.getUrl()).addPath("bookings").build();

    const response = await this.post(url, JSON.stringify(request), {
      headersVisitor: withApiVersion(ApiVersions.V1),
      authorize: true,
    });
    const json = await response.json();
    return BookingSchema.parse(json);
  }

  async fetchBookings(request?: FetchBookingsRequest) {
    const url = new UrlBuilder(this.getUrl())
      .addPath("bookings")
      .addQuery("from", request?.from?.toISOString())
      .addQuery("to", request?.to?.toISOString())
      .build();

    const response = await this.get(url, { authorize: true });
    const json = await response.json();

    const deserialized = json.map(BookingSchema.parse);

    return deserialized;
  }

  async fetchBooking(id: string) {
    const url = new UrlBuilder(this.getUrl())
      .addPath("bookings")
      .addPath(id)
      .build();

    const response = await this.get(url, { authorize: true });
    const json = await response.json();
    const booking = BookingSchema.parse(json);

    return booking;
  }

  async fetchStripeEphemeralKey(): Promise<StripeEphemeralKey> {
    const url = new UrlBuilder(this.getUrl())
      .addPath("stripe")
      .addPath("EphemeralKey")
      .build();

    const response = await this.get(url, { authorize: true });
    const json = await response.json();

    return json as StripeEphemeralKey;
  }

  protected getUrl() {
    return this.url;
  }

  async fetchFloorPlan(venueId: string): Promise<FloorPlan> {
    const url = new UrlBuilder(this.getUrl())
      .addPath("venues")
      .addPath(venueId)
      .addPath("floorplan")
      .build();

    const response = await this.get(url, { authorize: true });
    const json = await response.json();

    return this.floorPlanDeserializer.deserialize(json);
  }

  async fetchBookedTables(venueId: string, forDate: Date): Promise<TableId[]> {
    const url = new UrlBuilder(this.getUrl())
      .addPath("venues")
      .addPath(venueId)
      .addPath("bookedTables")
      .addPath(toISODateString(forDate))
      .build();

    const response = await this.get(url, { authorize: true });
    const json = await response.json();

    return json.map((j: any) => TableIdSchema.parse(j));
  }

  async fetchLockedTables(
    venueId: string,
    forDate: Date
  ): Promise<LockedTable[]> {
    const url = new UrlBuilder(this.getUrl())
      .addPath("venues")
      .addPath(venueId)
      .addPath("lockedTables")
      .addPath(toISODateString(forDate))
      .build();

    const response = await this.get(url, { authorize: true });
    const json = await response.json();
    return json.map(LockedTableSchema.parse);
  }

  /* ========================= type: Order ========================= */
  async fetchOrder(orderId: string): Promise<Order> {
    const url = new UrlBuilder(this.getUrl())
      .addPath("orders")
      .addPath(orderId)
      .build();

    const response = await this.get(url, { authorize: true });
    const result: any = await response.json();

    return this.orderDeserializer.deserialize(result);
  }

  async fetchOrderRejectionMessage(orderId: string): Promise<string> {
    const url = new UrlBuilder(this.getUrl())
      .addPath("orders")
      .addPath(orderId)
      .addPath("rejectionMessage")
      .build();

    return await this.fetchSingleRejection(url);
  }

  async fetchVenueByBooking(bookingId: string): Promise<Venue> {
    const url = new UrlBuilder(this.getUrl())
      .addPath("venues")
      .addPath("byBooking")
      .addPath(bookingId)
      .build();

    return await this.fetchSingleVenue(url);
  }

  async fetchVenueByOrder(orderId: string): Promise<Venue> {
    const url = new UrlBuilder(this.getUrl())
      .addPath("venues")
      .addPath("byOrder")
      .addPath(orderId)
      .build();

    return await this.fetchSingleVenue(url);
  }

  async fetchOrders(): Promise<Order[]> {
    const url = new UrlBuilder(this.getUrl()).addPath("orders").build();
    const response = await this.get(url, { authorize: true });
    const result: any = await response.json();
    return await Promise.all(this.orderDeserializer.deserializeAll(result));
  }

  /* ========================= type: User ========================= */
  async fetchIsUserComplete(): Promise<boolean> {
    const url = new UrlBuilder(this.getUrl())
      .addPath("user")
      .addPath("isComplete")
      .build();

    const response = await this.get(url, { authorize: true });
    const result = await response.json();
    return result.completed;
  }

  async updateCustomer(args: UpdateCustomerRequest): Promise<Customer> {
    const url = new UrlBuilder(this.getUrl()).addPath("customer").build();

    const body = JSON.stringify(args);
    const response = await this.post(url, body, { authorize: true });
    const json = await response.json();
    const parsed = CustomerSchema.parse(json);
    return parsed;
  }

  async updateUser(args: UpdateUserArgs): Promise<void> {
    const url = new UrlBuilder(this.getUrl()).addPath("user").build();
    await this.patch(url, JSON.stringify(args), { authorize: true });
  }

  async sendVerificationLink(): Promise<void> {
    const url = new UrlBuilder(this.getUrl())
      .addPath("user")
      .addPath("resendVerificationLink")
      .build();

    await this.post(url, EmptyBody, { authorize: true });
  }

  /* ========================= type: GreeterEvent ========================= */

  async fetchPreviousGreeterEventsForVenue({
    venueId,
    page,
    pageSize,
  }: PagedByVenueRequest): Promise<GreeterEvent[]> {
    const url = new UrlBuilder(this.getUrl())
      .addPath("greeterEvents")
      .addQuery("venueId", venueId)
      .addQuery("page", page)
      .addQuery("direction", "previous")
      .build();

    return await this.fetchMultipleEvents(url);
  }

  async fetchPreviousGreeterEvents({
    city,
    country,
    page,
    pageSize,
  }: PagedLocationRequest): Promise<GreeterEvent[]> {
    const url = new UrlBuilder(this.getUrl())
      .addPath("greeterEvents")
      .addQuery("page", page)
      .addQuery("pageSize", pageSize)
      .addQuery("city", city)
      .addQuery("country", country)
      .addQuery("direction", "previous")
      .build();

    return await this.fetchMultipleEvents(url);
  }

  async fetchUpcomingGreeterEventsForVenue({
    venueId,
    page,
    pageSize,
  }: PagedByVenueRequest): Promise<GreeterEvent[]> {
    const url = new UrlBuilder(this.getUrl())
      .addPath("greeterEvents")
      .addQuery("venueId", venueId)
      .addQuery("page", page)
      .addQuery("pageSize", pageSize)
      .addQuery("direction", "upcoming")
      .build();

    return await this.fetchMultipleEvents(url);
  }

  async fetchUpcomingGreeterEvents({
    city,
    country,
    page,
    pageSize,
  }: PagedLocationRequest): Promise<GreeterEvent[]> {
    const url = new UrlBuilder(this.getUrl())
      .addPath("greeterEvents")
      .addQuery("direction", "upcoming")
      .addQuery("page", page)
      .addQuery("pageSize", pageSize)
      .addQuery("city", city)
      .addQuery("country", country)
      .build();

    return await this.fetchMultipleEvents(url);
  }

  async fetchGreeterEvent(id: string): Promise<GreeterEvent> {
    const url = new UrlBuilder(this.getUrl())
      .addPath("greeterEvents")
      .addPath(id)
      .build();

    return await this.fetchSingleEvent(url);
  }

  /* ========================= type: Venue ========================= */
  async fetchSpecialOpeningHours(
    venueId: string
  ): Promise<SpecialOpeningHours[]> {
    const url = new UrlBuilder(this.getUrl())
      .addPath("venues")
      .addPath(venueId)
      .addPath("specialOpeningHours")
      .build();

    const response = await this.get(url);
    const json = await response.json();
    const parsed = ManySpecialOpeningHoursDeserializer.parse(json);

    const mapped = Array(parsed.length);
    for (let i = 0; i < parsed.length; i++) {
      const oh = parsed[i];
      mapped[i] = {
        id: oh.id,
        period: new DatePeriod({ from: oh.period.from, to: oh.period.to }),
      };
    }

    return mapped;
  }

  async fetchVenue(id: string): Promise<Venue> {
    const url = new UrlBuilder(this.getUrl())
      .addPath("venues")
      .addPath(id)
      .build();

    return await this.fetchSingleVenue(url);
  }

  async fetchVenues({
    ids,
    activityId,
    themeId,
    page,
    pageSize,
    city,
    country,
  }: VenueRequest) {
    const builder = new UrlBuilder(this.getUrl())
      .addPath("venues")
      .addQuery("ids", uniq(ids)?.join(","))
      .addQuery("activityId", activityId)
      .addQuery("themeId", themeId)
      .addQuery("page", page)
      .addQuery("pageSize", pageSize)
      .addQuery("city", city)
      .addQuery("country", country);

    const url = builder.build();

    return await this.fetchMultipleVenues(url);
  }

  async fetchVenueByEvent(id: string): Promise<Venue> {
    const url = new UrlBuilder(this.getUrl())
      .addPath("venues")
      .addPath("byEvent")
      .addPath(id)
      .build();

    return await this.fetchSingleVenue(url);
  }

  /* ========================= type: News ========================= */

  async fetchNews(): Promise<NewsItem[]> {
    const url = new UrlBuilder(this.getUrl()).addPath("news").build();
    return await this.fetchMultipleNews(url);
  }

  async fetchDaysliceConfig(): Promise<DaysliceConfig> {
    const url = new UrlBuilder(this.getUrl())
      .addPath("keyValues")
      .addPath("dayslices")
      .build();
    const response = await this.get(url);
    const json = await response.json();
    return json as DaysliceConfig;
  }

  /* ===== type: Activity ===== */

  async fetchActivities(): Promise<Activity[]> {
    const url = new UrlBuilder(this.getUrl()).addPath("activities").build();

    return await this.fetchMultipleActivities(url);
  }

  async fetchVenueActivities(venueId: string): Promise<VenueActivity[]> {
    const url = new UrlBuilder(this.getUrl())
      .addPath("venues")
      .addPath(venueId)
      .addPath("activities")
      .build();

    return await this.fetchMultipleVenueActivities(url);
  }

  /* ====== type: Theme ====== */
  async fetchThemes(request?: FetchThemesRequest): Promise<Theme[]> {
    const url = new UrlBuilder(this.getUrl())
      .addPath("themes")
      .addQuery("activityId", request?.activityId)
      .build();
    return await this.fetchMultipleThemes(url);
  }

  async fetchThemesByVenue(venueId: string) {
    const url = new UrlBuilder(this.getUrl())
      .addPath("themes")
      .addQuery("venueId", venueId)
      .build();

    return await this.fetchMultipleThemes(url);
  }

  /* ====== type: Product ===== */
  async fetchProductsByVenue(venueId: string): Promise<Product[]> {
    const url = new UrlBuilder(this.getUrl())
      .addPath("products")
      .addQuery("venueId", venueId)
      .build();

    return await this.fetchMultipleProducts(url);
  }

  async fetchSpecialPricePeriods(
    productIds: Array<string>
  ): Promise<Array<SpecialPricePeriod>> {
    const url = new UrlBuilder(this.getUrl())
      .addPath("specialPricePeriods")
      .addQuery("productIds", productIds)
      .build();

    const response = await this.get(url, { authorize: true });
    const json = await response.json();

    if (!Array.isArray(json)) {
      throw new Error("Wrong type for SpecialPricePeriods, we need an array");
    }

    const parsed = json.map((j) => SpecialPricePeriodSchema.parse(j));

    return parsed;
  }

  async fetchMusicGenres(request: PageRequest): Promise<MusicGenre[]> {
    const url = new UrlBuilder(this.getUrl())
      .addPath("musicgenres")
      .addQuery("page", request?.page)
      .addQuery("pageSize", request?.pageSize)
      .build();

    const response = await this.get(url);
    const json = await response.json();

    return json as MusicGenre[];
  }

  /* ===== Util stuff ===== */
  private async fetchSingleEvent(url: string): Promise<GreeterEvent> {
    const result = await this.get(url);
    const json = await result.json();
    return GreeterEventSchema.parse(json);
  }

  private async fetchMultipleEvents(url: string): Promise<GreeterEvent[]> {
    const result = await this.get(url);
    const json = await result.json();
    return json.map(GreeterEventSchema.parse);
  }

  private async fetchSingleVenue(url: string): Promise<Venue> {
    const result = await this.get(url);
    const json = await result.json();
    return VenueSchema.parse(json);
  }

  private async fetchSingleRejection(url: string): Promise<string> {
    const result = await this.get(url, { authorize: true });
    const json = await result.json();
    return json.message;
  }

  private async fetchMultipleVenues(url: string): Promise<Venue[]> {
    const json = await (await this.get(url)).json();
    return json.map(VenueSchema.parse);
  }

  private async fetchMultipleActivities(url: string): Promise<Activity[]> {
    const result = await this.get(url);
    const json = await result.json();
    return this.createActivitiesFromJsonList(json);
  }

  private async fetchMultipleVenueActivities(
    url: string
  ): Promise<VenueActivity[]> {
    const result = await this.get(url);
    const json = await result.json();
    return this.createVenueActivitiesFromJsonList(json);
  }

  private async fetchMultipleNews(url: string): Promise<NewsItem[]> {
    const result = await this.get(url);
    const json = await result.json();
    return this.createNewsItemsFromJson(json);
  }

  private async fetchMultipleThemes(url: string): Promise<Theme[]> {
    const result = await this.get(url);
    const json = await result.json();
    return json.map(ThemeSchema.parse);
  }

  private async fetchMultipleProducts(url: string): Promise<Product[]> {
    const result = await this.get(url);
    const json = await result.json();
    const parsed = json.map(ProductSchema.parse);
    return Product.sortByPriceAndPriority(parsed);
  }

  private createNewsItemsFromJson(json: any): NewsItem[] {
    const newsItems = json.map((newsItem: any) => {
      return {
        title: newsItem.title,
        body: newsItem.body,
        imageUrl: newsItem.imageUrl,
        link: {
          type: newsItem.linkType,
          data: newsItem.linkData,
        },
      };
    });
    return newsItems;
  }

  private createConfigFromJson<T>(config: any, json: any): T {
    return Object.assign(config, json) as T;
  }

  private createStringFromJson(json: any): string {
    return json.message;
  }

  private createActivityFromJson(json: any): Activity {
    return { id: json.id, name: json.name, coverUrl: json.coverUrl };
  }

  private createVenueActivityFromJson(json: any): VenueActivity {
    //Is this to overcomplicate? Pro is that if Activity changes, we dont have to change this stuff.
    //But on the other hand, we still need to change the class VenueActivity, to adapt to Activity's change
    const va = this.createActivityFromJson(json) as VenueActivity;
    va.description = json.description;
    return va;
  }

  private createActivitiesFromJsonList(json: any): Activity[] {
    const out = json.map((el: unknown) => this.createActivityFromJson(el));
    return out;
  }

  private createVenueActivitiesFromJsonList(json: any): VenueActivity[] {
    const out = json.map((el: unknown) => this.createVenueActivityFromJson(el));
    return out;
  }
}
